import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Races } from '../races/Races.js';
import { BloodParticleSystem } from '../effects/BloodParticles.js';
import { audioManager } from '../audio/AudioManager.js';

export class Enemy {
    constructor(scene, world, race, target, pathfinding, callbacks = {}) {
        this.scene = scene;
        this.world = world;
        this.race = race;
        this.raceData = Races[race];
        this.target = target; // Player (primary target)
        this.pathfinding = pathfinding;
        this.callbacks = callbacks;
        this.getAllies = callbacks.getAllies || (() => []);
        this.currentTarget = null; // Current target (player or ally)
        this.groundBoundary = callbacks.groundBoundary || 48; // Ground boundary to stay within
        
        // Create enemy mesh based on race
        this.mesh = this.createEnemyMesh();
        this.mesh.position.set(0, 1, 0);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        // Mark as enemy for raycast detection
        this.mesh.userData.isEnemy = true;
        this.mesh.userData.enemyInstance = this;
        this.scene.add(this.mesh);
        
        // Physics body
        const shape = new CANNON.Box(new CANNON.Vec3(0.5, 1, 0.5));
        this.body = new CANNON.Body({ mass: 1 });
        this.body.addShape(shape);
        this.body.position.set(0, 1, 0);
        this.body.material = new CANNON.Material('enemy');
        this.world.addBody(this.body);
        
        // Apply difficulty multiplier
        const difficultyMultiplier = callbacks.difficultyMultiplier || 1.0;
        const difficulty = callbacks.difficulty || 'medium';
        this.health = Math.floor((this.raceData.health || 50) * difficultyMultiplier);
        this.maxHealth = this.health;
        // Slower movement on easy mode
        const baseSpeed = this.raceData.moveSpeed || 2;
        this.moveSpeed = difficulty === 'easy' ? baseSpeed * 0.7 : (difficulty === 'hard' ? baseSpeed * 1.2 : baseSpeed);
        
        this.position = this.mesh.position;
        this.isDead = false;
        
        // AI state
        this.path = [];
        this.currentPathIndex = 0;
        this.pathUpdateTimer = 0;
        this.pathUpdateInterval = 1.0; // Update path every second
        
        // Blood system
        this.bloodSystem = new BloodParticleSystem(scene);
        this.lastPosition = new THREE.Vector3().copy(this.position);
        this.isWounded = false;
        
        // Attack system
        this.attackRange = 2.5; // Attack range in units
        this.attackDamage = Math.floor((this.raceData.attackDamage || 12) * difficultyMultiplier);
        // Longer cooldown on easy mode (difficulty already declared above)
        this.attackCooldown = difficulty === 'easy' ? 2.5 : (difficulty === 'hard' ? 1.0 : 1.5); // Seconds between attacks
        this.lastAttackTime = 0;
        this.isAttacking = false;
        
        // Target search
        this.targetSearchTimer = 0;
        this.targetSearchInterval = 0.5; // Search for new target every 0.5 seconds
        
        // Animation states
        this.isWalking = false;
        this.walkAnimationTime = 0;
        this.isDying = false;
        this.deathTime = 0;
        this.attackAnimationTime = 0;
        
        // Original mesh rotation for animation
        this.originalRotation = { x: 0, y: 0, z: 0 };
    }
    
    update(deltaTime) {
        if (this.isDead) {
            // Handle death animation (falling)
            if (this.isDying && this.mesh) {
                const timeSinceDeath = (Date.now() - this.deathTime) / 1000;
                
                // Rotate to lie down
                if (timeSinceDeath < 0.5) {
                    const fallProgress = timeSinceDeath / 0.5;
                    this.mesh.rotation.x = Math.PI / 2 * fallProgress; // Rotate to lie flat
                    this.mesh.position.y = 1 - (1 * fallProgress); // Fall to ground
                } else {
                    // Fully dead - lying on ground
                    this.mesh.rotation.x = Math.PI / 2;
                    this.mesh.position.y = 0.1;
                }
            }
            return;
        }
        
        if (!this.target) return;
        
        // Find nearest target (player or ally)
        this.targetSearchTimer += deltaTime;
        if (this.targetSearchTimer >= this.targetSearchInterval) {
            this.findNearestTarget();
            this.targetSearchTimer = 0;
        }
        
        // Determine target position
        let targetPosition = null;
        if (this.currentTarget && this.currentTarget.mesh && !this.currentTarget.isDead) {
            // Target is an ally
            targetPosition = this.currentTarget.position;
        } else if (this.target && this.target.camera) {
            // Target is player
            targetPosition = this.target.camera.position;
        }
        
        if (!targetPosition) return;
        
        // Update pathfinding
        this.pathUpdateTimer += deltaTime;
        if (this.pathUpdateTimer >= this.pathUpdateInterval) {
            this.updatePath(targetPosition);
            this.pathUpdateTimer = 0;
        }
        
        // Follow path or move directly to target
        let direction = new THREE.Vector3();
        
        if (this.path.length > 0 && this.currentPathIndex < this.path.length) {
            const targetPos = this.path[this.currentPathIndex];
            direction.subVectors(targetPos, this.position);
            direction.y = 0;
            
            const distance = direction.length();
            if (distance < 0.5) {
                this.currentPathIndex++;
            }
        } else {
            // Direct movement to target
            direction.subVectors(targetPosition, this.position);
            direction.y = 0;
        }
        
        if (direction.length() > 0.1) {
            direction.normalize();
            
            // Move towards target
            const moveVector = direction.multiplyScalar(this.moveSpeed);
            this.body.velocity.x = moveVector.x;
            this.body.velocity.z = moveVector.z;
            
            // Sync mesh with physics body
            this.mesh.position.x = this.body.position.x;
            this.mesh.position.y = this.body.position.y;
            this.mesh.position.z = this.body.position.z;
            
            // Boundary check - prevent enemy from leaving the ground area
            if (this.body.position.x > this.groundBoundary) {
                this.body.position.x = this.groundBoundary;
                this.body.velocity.x = 0;
            }
            if (this.body.position.x < -this.groundBoundary) {
                this.body.position.x = -this.groundBoundary;
                this.body.velocity.x = 0;
            }
            if (this.body.position.z > this.groundBoundary) {
                this.body.position.z = this.groundBoundary;
                this.body.velocity.z = 0;
            }
            if (this.body.position.z < -this.groundBoundary) {
                this.body.position.z = -this.groundBoundary;
                this.body.velocity.z = 0;
            }
            
            // Sync mesh after boundary check
            this.mesh.position.x = this.body.position.x;
            this.mesh.position.z = this.body.position.z;
            
            // Look at target
            this.mesh.lookAt(targetPosition);
            
            // Walking animation
            this.isWalking = true;
            this.walkAnimationTime += deltaTime * 8; // Animation speed
            // Slight bobbing motion while walking
            const bobAmount = Math.sin(this.walkAnimationTime) * 0.05;
            this.mesh.position.y = this.body.position.y + bobAmount;
        } else {
            this.isWalking = false;
        }
        
        // Attack animation
        if (this.isAttacking) {
            this.attackAnimationTime += deltaTime * 10;
            // Slight forward lunge during attack
            const lungeAmount = Math.sin(this.attackAnimationTime) * 0.2;
            if (this.mesh && this.currentTarget) {
                const attackDir = new THREE.Vector3()
                    .subVectors(this.currentTarget.position || this.target.camera.position, this.position)
                    .normalize();
                this.mesh.position.add(attackDir.multiplyScalar(lungeAmount * deltaTime));
            }
        }
        
        // Create blood trail if wounded
        if (this.isWounded) {
            const distance = this.lastPosition.distanceTo(this.position);
            if (distance > 0.5) {
                this.bloodSystem.createBloodTrail(this.lastPosition, this.position);
                this.lastPosition.copy(this.position);
            }
        }
        
        // Attack target if in range
        const distanceToTarget = this.position.distanceTo(targetPosition);
        if (distanceToTarget <= this.attackRange) {
            // Stop moving when in attack range
            this.body.velocity.x = 0;
            this.body.velocity.z = 0;
            
            // Attack target
            const currentTime = Date.now() / 1000;
            if (currentTime - this.lastAttackTime >= this.attackCooldown) {
                if (this.currentTarget && this.currentTarget.mesh && !this.currentTarget.isDead) {
                    // Attack ally
                    this.attackAlly(this.currentTarget);
                } else {
                    // Attack player
                    this.attackPlayer();
                }
                this.lastAttackTime = currentTime;
            }
        }
        
        // Update blood particles
        this.bloodSystem.update(deltaTime);
    }
    
    findNearestTarget() {
        // Check for nearby allies first (they're easier targets)
        const allies = this.getAllies();
        let nearestAlly = null;
        let nearestAllyDistance = Infinity;
        
        for (const ally of allies) {
            if (ally.isDead || !ally.mesh) continue;
            
            const distance = this.position.distanceTo(ally.position);
            if (distance < nearestAllyDistance && distance < 20) {
                nearestAllyDistance = distance;
                nearestAlly = ally;
            }
        }
        
        // Check distance to player
        let playerDistance = Infinity;
        if (this.target && this.target.camera) {
            playerDistance = this.position.distanceTo(this.target.camera.position);
        }
        
        // Choose closest target (ally or player)
        if (nearestAlly && nearestAllyDistance < playerDistance) {
            this.currentTarget = nearestAlly;
        } else {
            this.currentTarget = null; // Target player
        }
    }
    
    attackAlly(ally) {
        if (!ally || ally.isDead || this.isDead) return;
        
        this.isAttacking = true;
        
        // Apply damage to ally
        const damage = this.attackDamage + Math.random() * 3;
        const hitPosition = ally.position.clone();
        const hitDirection = new THREE.Vector3()
            .subVectors(hitPosition, this.position)
            .normalize();
        
        ally.takeDamage(damage, hitPosition, hitDirection);
        
        // Play attack sound
        audioManager.playSound('enemy_attack', {
            position: this.position,
            volume: 0.6
        });
        
        // Reset attack flag
        setTimeout(() => {
            this.isAttacking = false;
        }, 200);
    }
    
    attackPlayer() {
        if (!this.target || this.isDead) return;
        
        this.isAttacking = true;
        
        // Apply damage to player (reduced damage so player needs multiple hits to die)
        const damage = this.attackDamage + Math.random() * 3; // Reduced variation: 3 instead of 5
        this.target.takeDamage(damage);
        
        // Create blood effect at player position
        const playerPos = this.target.camera.position.clone();
        const attackDirection = new THREE.Vector3()
            .subVectors(playerPos, this.position)
            .normalize();
        
        // Blood splatter effect
        this.bloodSystem.createBloodSplatter(
            playerPos,
            attackDirection,
            5
        );
        
        // Play attack sound
        audioManager.playSound('enemy_attack', {
            position: this.position,
            volume: 0.6
        });
        
        // Reset attack flag after a short delay
        setTimeout(() => {
            this.isAttacking = false;
        }, 200);
    }
    
    updatePath(targetPosition = null) {
        if (!this.pathfinding) return;
        
        if (!targetPosition) {
            if (this.currentTarget && this.currentTarget.mesh) {
                targetPosition = this.currentTarget.position;
            } else if (this.target && this.target.camera) {
                targetPosition = this.target.camera.position;
            } else {
                return;
            }
        }
        
        const start = { x: this.position.x, z: this.position.z };
        const end = { x: targetPosition.x, z: targetPosition.z };
        
        this.path = this.pathfinding.findPath(start, end);
        this.currentPathIndex = 0;
    }
    
    takeDamage(amount, hitPosition, hitDirection) {
        this.health -= amount;
        this.isWounded = true;
        
        // Create blood splatter
        if (hitPosition && hitDirection) {
            this.bloodSystem.createBloodSplatter(
                hitPosition,
                hitDirection,
                Math.min(amount / 10, 10)
            );
            
            // Create blood decal on ground
            const groundHit = hitPosition.clone();
            groundHit.y = 0.1;
            this.bloodSystem.createBloodDecal(groundHit, new THREE.Vector3(0, 1, 0), 0.5);
        }
        
        if (this.health <= 0) {
            this.die(hitPosition, hitDirection);
        }
    }
    
    die(hitPosition, hitDirection) {
        if (this.isDead) return;
        
        this.isDead = true;
        this.isDying = true;
        this.deathTime = Date.now();
        
        // Massive blood splatter on death
        if (hitPosition && hitDirection) {
            this.bloodSystem.createBloodSplatter(
                hitPosition,
                hitDirection,
                15
            );
        } else {
            // Default death splatter
            const deathPos = this.position.clone();
            deathPos.y = 1;
            this.bloodSystem.createBloodSplatter(
                deathPos,
                new THREE.Vector3(0, -1, 0),
                15
            );
        }
        
        // Create large blood pool on ground
        const groundPos = this.position.clone();
        groundPos.y = 0.01;
        this.bloodSystem.createBloodPool(groundPos, 1.5 + Math.random() * 0.5);
        
        // Create multiple blood decals around death position
        for (let i = 0; i < 5; i++) {
            const angle = (i / 5) * Math.PI * 2;
            const offset = new THREE.Vector3(
                Math.cos(angle) * (0.5 + Math.random() * 0.5),
                0.01,
                Math.sin(angle) * (0.5 + Math.random() * 0.5)
            );
            const decalPos = groundPos.clone().add(offset);
            this.bloodSystem.createBloodDecal(decalPos, new THREE.Vector3(0, 1, 0), 0.3 + Math.random() * 0.3);
        }
        
        // Stop physics body movement
        if (this.body) {
            this.body.velocity.set(0, 0, 0);
            this.body.angularVelocity.set(0, 0, 0);
        }
        
        // Callback
        this.callbacks.onDeath?.();
        
        // DON'T remove from scene - let it lie there dead
        // Just mark as dead and let it fall/rotate
    }
    
    createEnemyMesh() {
        const group = new THREE.Group();
        const baseColor = this.raceData.color || 0xff0000;
        
        if (this.race === 'elf') {
            // Create elven character - tall, slender, with pointed ears
            const material = new THREE.MeshStandardMaterial({ 
                color: baseColor,
                metalness: 0.3,
                roughness: 0.7
            });
            
            // Head (smaller, more elegant)
            const headGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.4);
            const head = new THREE.Mesh(headGeometry, material);
            head.position.set(0, 1.6, 0);
            group.add(head);
            
            // Pointed ears (left)
            const earGeometry = new THREE.ConeGeometry(0.08, 0.3, 3);
            const leftEar = new THREE.Mesh(earGeometry, material);
            leftEar.rotation.z = Math.PI / 6;
            leftEar.position.set(-0.25, 1.75, 0);
            group.add(leftEar);
            
            // Pointed ears (right)
            const rightEar = new THREE.Mesh(earGeometry, material);
            rightEar.rotation.z = -Math.PI / 6;
            rightEar.position.set(0.25, 1.75, 0);
            group.add(rightEar);
            
            // Body (slender and tall)
            const bodyGeometry = new THREE.BoxGeometry(0.5, 0.8, 0.3);
            const body = new THREE.Mesh(bodyGeometry, material);
            body.position.set(0, 1.0, 0);
            group.add(body);
            
            // Arms (slender)
            const armGeometry = new THREE.BoxGeometry(0.15, 0.6, 0.15);
            const leftArm = new THREE.Mesh(armGeometry, material);
            leftArm.position.set(-0.35, 0.9, 0);
            group.add(leftArm);
            
            const rightArm = new THREE.Mesh(armGeometry, material);
            rightArm.position.set(0.35, 0.9, 0);
            group.add(rightArm);
            
            // Legs (slender)
            const legGeometry = new THREE.BoxGeometry(0.2, 0.6, 0.2);
            const leftLeg = new THREE.Mesh(legGeometry, material);
            leftLeg.position.set(-0.15, 0.2, 0);
            group.add(leftLeg);
            
            const rightLeg = new THREE.Mesh(legGeometry, material);
            rightLeg.position.set(0.15, 0.2, 0);
            group.add(rightLeg);
            
            // Optional: Cape or cloak (for elegance)
            const capeGeometry = new THREE.BoxGeometry(0.6, 0.9, 0.1);
            const capeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x2a2a2a,
                metalness: 0.1,
                roughness: 0.9
            });
            const cape = new THREE.Mesh(capeGeometry, capeMaterial);
            cape.position.set(0, 0.95, -0.2);
            group.add(cape);
            
            // Bow and Arrow (elven weapon)
            // Bow
            const bowGeometry = new THREE.BoxGeometry(0.08, 0.6, 0.08);
            const bowMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x8B4513,
                roughness: 0.7
            });
            const bow = new THREE.Mesh(bowGeometry, bowMaterial);
            bow.position.set(0.4, 0.9, 0);
            bow.rotation.z = Math.PI / 4;
            group.add(bow);
            
            // Bow string (thin cylinder)
            const stringGeometry = new THREE.CylinderGeometry(0.01, 0.01, 0.5, 8);
            const stringMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xffffff,
                roughness: 0.5
            });
            const bowString = new THREE.Mesh(stringGeometry, stringMaterial);
            bowString.rotation.x = Math.PI / 2;
            bowString.position.set(0.4, 0.9, 0);
            group.add(bowString);
            
            // Arrow (in hand)
            const arrowShaftGeometry = new THREE.CylinderGeometry(0.01, 0.01, 0.3, 8);
            const arrowMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x654321,
                roughness: 0.6
            });
            const arrow = new THREE.Mesh(arrowShaftGeometry, arrowMaterial);
            arrow.rotation.x = Math.PI / 2;
            arrow.position.set(0.4, 0.9, 0.1);
            group.add(arrow);
            
            // Arrow head
            const arrowHeadGeometry = new THREE.ConeGeometry(0.02, 0.05, 6);
            const arrowHeadMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x888888,
                metalness: 0.7,
                roughness: 0.3
            });
            const arrowHead = new THREE.Mesh(arrowHeadGeometry, arrowHeadMaterial);
            arrowHead.rotation.x = -Math.PI / 2;
            arrowHead.position.set(0.4, 0.9, 0.25);
            group.add(arrowHead);
            
        } else if (this.race === 'orc') {
            // Orcs - broader, more muscular with characteristic features
            const skinColor = 0x2d5016; // Darker green for orc skin
            const skinMaterial = new THREE.MeshStandardMaterial({ 
                color: skinColor,
                roughness: 0.9,
                metalness: 0.1
            });
            
            const darkerGreen = 0x1a3d0f; // Even darker for details
            const detailMaterial = new THREE.MeshStandardMaterial({ 
                color: darkerGreen,
                roughness: 0.9
            });
            
            // Head (larger, more square with orc features)
            const headGeometry = new THREE.BoxGeometry(0.65, 0.7, 0.6);
            const head = new THREE.Mesh(headGeometry, skinMaterial);
            head.position.set(0, 1.5, 0);
            group.add(head);
            
            // Lower jaw (protruding, orc-like)
            const jawGeometry = new THREE.BoxGeometry(0.5, 0.25, 0.4);
            const jaw = new THREE.Mesh(jawGeometry, skinMaterial);
            jaw.position.set(0, 1.35, 0.15);
            group.add(jaw);
            
            // Tusks (characteristic orc tusks)
            const tuskGeometry = new THREE.ConeGeometry(0.05, 0.15, 6);
            const tuskMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xffffff,
                roughness: 0.3
            });
            
            const leftTusk = new THREE.Mesh(tuskGeometry, tuskMaterial);
            leftTusk.rotation.z = Math.PI / 4;
            leftTusk.position.set(-0.2, 1.4, 0.35);
            group.add(leftTusk);
            
            const rightTusk = new THREE.Mesh(tuskGeometry, tuskMaterial);
            rightTusk.rotation.z = -Math.PI / 4;
            rightTusk.position.set(0.2, 1.4, 0.35);
            group.add(rightTusk);
            
            // Eyes (glowing red)
            const eyeGeometry = new THREE.SphereGeometry(0.08, 8, 8);
            const eyeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xff0000,
                emissive: 0xff0000,
                emissiveIntensity: 0.5
            });
            
            const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            leftEye.position.set(-0.15, 1.55, 0.32);
            group.add(leftEye);
            
            const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            rightEye.position.set(0.15, 1.55, 0.32);
            group.add(rightEye);
            
            // Body (broad and muscular with armor/details)
            const bodyGeometry = new THREE.BoxGeometry(0.75, 1.0, 0.55);
            const body = new THREE.Mesh(bodyGeometry, skinMaterial);
            body.position.set(0, 0.9, 0);
            group.add(body);
            
            // Chest muscles/details
            const chestDetail = new THREE.Mesh(
                new THREE.BoxGeometry(0.5, 0.3, 0.1),
                detailMaterial
            );
            chestDetail.position.set(0, 1.0, 0.3);
            group.add(chestDetail);
            
            // Arms (thick and muscular)
            const armGeometry = new THREE.BoxGeometry(0.28, 0.75, 0.28);
            const leftArm = new THREE.Mesh(armGeometry, skinMaterial);
            leftArm.position.set(-0.48, 0.85, 0);
            group.add(leftArm);
            
            const rightArm = new THREE.Mesh(armGeometry, skinMaterial);
            rightArm.position.set(0.48, 0.85, 0);
            group.add(rightArm);
            
            // Shoulder pads (armor-like)
            const shoulderGeometry = new THREE.BoxGeometry(0.35, 0.25, 0.35);
            const shoulderMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x4a4a4a,
                metalness: 0.6,
                roughness: 0.4
            });
            
            const leftShoulder = new THREE.Mesh(shoulderGeometry, shoulderMaterial);
            leftShoulder.position.set(-0.48, 1.2, 0);
            group.add(leftShoulder);
            
            const rightShoulder = new THREE.Mesh(shoulderGeometry, shoulderMaterial);
            rightShoulder.position.set(0.48, 1.2, 0);
            group.add(rightShoulder);
            
            // Legs (thick and powerful)
            const legGeometry = new THREE.BoxGeometry(0.32, 0.75, 0.32);
            const leftLeg = new THREE.Mesh(legGeometry, skinMaterial);
            leftLeg.position.set(-0.22, 0.15, 0);
            group.add(leftLeg);
            
            const rightLeg = new THREE.Mesh(legGeometry, skinMaterial);
            rightLeg.position.set(0.22, 0.15, 0);
            group.add(rightLeg);
            
            // Club (orc weapon - crude knuppel)
            const clubGeometry = new THREE.BoxGeometry(0.18, 0.9, 0.18);
            const clubMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x654321,
                roughness: 0.9
            });
            const club = new THREE.Mesh(clubGeometry, clubMaterial);
            club.position.set(0.6, 0.85, 0);
            club.rotation.z = Math.PI / 6;
            group.add(club);
            
            // Club head (thick and heavy)
            const clubHeadGeometry = new THREE.BoxGeometry(0.3, 0.25, 0.3);
            const clubHead = new THREE.Mesh(clubHeadGeometry, clubMaterial);
            clubHead.position.set(0.6, 1.2, 0);
            clubHead.rotation.z = Math.PI / 6;
            group.add(clubHead);
            
            // Spikes on club (crude)
            const spikeGeometry = new THREE.ConeGeometry(0.04, 0.1, 4);
            const spikeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x4a4a4a,
                metalness: 0.4,
                roughness: 0.6
            });
            
            for (let i = 0; i < 3; i++) {
                const spike = new THREE.Mesh(spikeGeometry, spikeMaterial);
                const angle = (i / 3) * Math.PI * 2;
                spike.position.set(
                    0.6 + Math.cos(angle) * 0.12,
                    1.25,
                    Math.sin(angle) * 0.12
                );
                spike.rotation.z = Math.PI / 6;
                group.add(spike);
            }
            
        } else if (this.race === 'troll') {
            // Trolls - very large, ugly, with characteristic features
            const skinColor = 0x6b4423; // Brownish-gray troll skin
            const skinMaterial = new THREE.MeshStandardMaterial({ 
                color: skinColor,
                roughness: 0.95,
                metalness: 0.05
            });
            
            const darkerSkin = 0x4a2e15; // Darker for details
            const detailMaterial = new THREE.MeshStandardMaterial({ 
                color: darkerSkin,
                roughness: 0.95
            });
            
            // Head (very large, ugly, with big nose)
            const headGeometry = new THREE.BoxGeometry(0.85, 0.9, 0.75);
            const head = new THREE.Mesh(headGeometry, skinMaterial);
            head.position.set(0, 1.65, 0);
            group.add(head);
            
            // Big ugly nose (characteristic troll feature)
            const noseGeometry = new THREE.BoxGeometry(0.2, 0.3, 0.25);
            const nose = new THREE.Mesh(noseGeometry, detailMaterial);
            nose.position.set(0, 1.6, 0.4);
            group.add(nose);
            
            // Small beady eyes
            const eyeGeometry = new THREE.SphereGeometry(0.1, 8, 8);
            const eyeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x000000,
                emissive: 0x000000
            });
            
            const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            leftEye.position.set(-0.2, 1.7, 0.38);
            group.add(leftEye);
            
            const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            rightEye.position.set(0.2, 1.7, 0.38);
            group.add(rightEye);
            
            // Large mouth/teeth
            const mouthGeometry = new THREE.BoxGeometry(0.4, 0.15, 0.1);
            const mouth = new THREE.Mesh(mouthGeometry, detailMaterial);
            mouth.position.set(0, 1.5, 0.38);
            group.add(mouth);
            
            // Big teeth
            const toothGeometry = new THREE.BoxGeometry(0.08, 0.12, 0.08);
            const toothMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xffffcc,
                roughness: 0.5
            });
            
            const leftTooth = new THREE.Mesh(toothGeometry, toothMaterial);
            leftTooth.position.set(-0.1, 1.45, 0.42);
            group.add(leftTooth);
            
            const rightTooth = new THREE.Mesh(toothGeometry, toothMaterial);
            rightTooth.position.set(0.1, 1.45, 0.42);
            group.add(rightTooth);
            
            // Rough hair/beard (spiky)
            const hairGeometry = new THREE.BoxGeometry(0.7, 0.2, 0.1);
            const hairMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x3d2817,
                roughness: 0.9
            });
            
            const hair = new THREE.Mesh(hairGeometry, hairMaterial);
            hair.position.set(0, 1.85, 0.1);
            group.add(hair);
            
            // Body (huge, barrel-chested)
            const bodyGeometry = new THREE.BoxGeometry(1.1, 1.3, 0.8);
            const body = new THREE.Mesh(bodyGeometry, skinMaterial);
            body.position.set(0, 0.75, 0);
            group.add(body);
            
            // Belly (protruding)
            const bellyGeometry = new THREE.BoxGeometry(0.9, 0.5, 0.6);
            const belly = new THREE.Mesh(bellyGeometry, detailMaterial);
            belly.position.set(0, 0.5, 0.2);
            group.add(belly);
            
            // Arms (very long, almost to ground, thick)
            const upperArmGeometry = new THREE.BoxGeometry(0.4, 0.6, 0.4);
            const lowerArmGeometry = new THREE.BoxGeometry(0.35, 0.5, 0.35);
            
            // Left arm
            const leftUpperArm = new THREE.Mesh(upperArmGeometry, skinMaterial);
            leftUpperArm.position.set(-0.65, 0.7, 0);
            group.add(leftUpperArm);
            
            const leftLowerArm = new THREE.Mesh(lowerArmGeometry, skinMaterial);
            leftLowerArm.position.set(-0.65, 0.2, 0);
            group.add(leftLowerArm);
            
            // Right arm
            const rightUpperArm = new THREE.Mesh(upperArmGeometry, skinMaterial);
            rightUpperArm.position.set(0.65, 0.7, 0);
            group.add(rightUpperArm);
            
            const rightLowerArm = new THREE.Mesh(lowerArmGeometry, skinMaterial);
            rightLowerArm.position.set(0.65, 0.2, 0);
            group.add(rightLowerArm);
            
            // Big hands
            const handGeometry = new THREE.BoxGeometry(0.4, 0.3, 0.4);
            const leftHand = new THREE.Mesh(handGeometry, skinMaterial);
            leftHand.position.set(-0.65, -0.05, 0);
            group.add(leftHand);
            
            const rightHand = new THREE.Mesh(handGeometry, skinMaterial);
            rightHand.position.set(0.65, -0.05, 0);
            group.add(rightHand);
            
            // Legs (very thick, short)
            const legGeometry = new THREE.BoxGeometry(0.45, 0.95, 0.45);
            const leftLeg = new THREE.Mesh(legGeometry, skinMaterial);
            leftLeg.position.set(-0.28, 0.0, 0);
            group.add(leftLeg);
            
            const rightLeg = new THREE.Mesh(legGeometry, skinMaterial);
            rightLeg.position.set(0.28, 0.0, 0);
            group.add(rightLeg);
            
            // Big feet
            const footGeometry = new THREE.BoxGeometry(0.5, 0.2, 0.6);
            const leftFoot = new THREE.Mesh(footGeometry, detailMaterial);
            leftFoot.position.set(-0.28, -0.5, 0.1);
            group.add(leftFoot);
            
            const rightFoot = new THREE.Mesh(footGeometry, detailMaterial);
            rightFoot.position.set(0.28, -0.5, 0.1);
            group.add(rightFoot);
            
            // Large Axe (troll weapon)
            const axeHandleGeometry = new THREE.BoxGeometry(0.25, 1.3, 0.25);
            const axeHandleMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x3d2817,
                roughness: 0.9
            });
            const axeHandle = new THREE.Mesh(axeHandleGeometry, axeHandleMaterial);
            axeHandle.position.set(0.8, 0.35, 0);
            axeHandle.rotation.z = Math.PI / 8;
            group.add(axeHandle);
            
            // Axe head (large and heavy)
            const axeHeadGeometry = new THREE.BoxGeometry(0.5, 0.4, 0.15);
            const axeHeadMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x666666,
                metalness: 0.6,
                roughness: 0.4
            });
            const axeHead = new THREE.Mesh(axeHeadGeometry, axeHeadMaterial);
            axeHead.position.set(0.8, 0.95, 0);
            axeHead.rotation.z = Math.PI / 8;
            group.add(axeHead);
            
            // Axe blade edge (sharp)
            const bladeGeometry = new THREE.BoxGeometry(0.55, 0.1, 0.05);
            const bladeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xaaaaaa,
                metalness: 0.8,
                roughness: 0.2
            });
            const blade = new THREE.Mesh(bladeGeometry, bladeMaterial);
            blade.position.set(0.8, 1.05, 0.1);
            blade.rotation.z = Math.PI / 8;
            group.add(blade);
            
        } else {
            // Default: Human - with armor, helmet, and face
            const skinColor = 0xffdbac; // Skin tone
            const skinMaterial = new THREE.MeshStandardMaterial({ 
                color: skinColor,
                roughness: 0.8
            });
            
            const armorColor = 0x888888; // Gray metal
            const armorMaterial = new THREE.MeshStandardMaterial({ 
                color: armorColor,
                metalness: 0.7,
                roughness: 0.3
            });
            
            // Head (with face)
            const headGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
            const head = new THREE.Mesh(headGeometry, skinMaterial);
            head.position.set(0, 1.5, 0);
            group.add(head);
            
            // Face features - Eyes
            const eyeGeometry = new THREE.SphereGeometry(0.05, 8, 8);
            const eyeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x000000
            });
            
            const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            leftEye.position.set(-0.12, 1.52, 0.26);
            group.add(leftEye);
            
            const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            rightEye.position.set(0.12, 1.52, 0.26);
            group.add(rightEye);
            
            // Nose
            const noseGeometry = new THREE.BoxGeometry(0.08, 0.1, 0.05);
            const nose = new THREE.Mesh(noseGeometry, skinMaterial);
            nose.position.set(0, 1.48, 0.26);
            group.add(nose);
            
            // Mouth
            const mouthGeometry = new THREE.BoxGeometry(0.15, 0.03, 0.02);
            const mouthMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x8b0000
            });
            const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial);
            mouth.position.set(0, 1.42, 0.25);
            group.add(mouth);
            
            // Helmet
            const helmetGeometry = new THREE.BoxGeometry(0.55, 0.4, 0.55);
            const helmet = new THREE.Mesh(helmetGeometry, armorMaterial);
            helmet.position.set(0, 1.65, 0);
            group.add(helmet);
            
            // Helmet visor/face guard
            const visorGeometry = new THREE.BoxGeometry(0.5, 0.15, 0.05);
            const visor = new THREE.Mesh(visorGeometry, armorMaterial);
            visor.position.set(0, 1.55, 0.23);
            group.add(visor);
            
            // Body (with armor)
            const bodyGeometry = new THREE.BoxGeometry(0.6, 0.9, 0.4);
            const body = new THREE.Mesh(bodyGeometry, skinMaterial);
            body.position.set(0, 0.9, 0);
            group.add(body);
            
            // Chest plate (armor)
            const chestPlateGeometry = new THREE.BoxGeometry(0.55, 0.5, 0.15);
            const chestPlate = new THREE.Mesh(chestPlateGeometry, armorMaterial);
            chestPlate.position.set(0, 1.05, 0.2);
            group.add(chestPlate);
            
            // Shoulder pads (armor)
            const shoulderGeometry = new THREE.BoxGeometry(0.3, 0.2, 0.3);
            const leftShoulder = new THREE.Mesh(shoulderGeometry, armorMaterial);
            leftShoulder.position.set(-0.35, 1.15, 0);
            group.add(leftShoulder);
            
            const rightShoulder = new THREE.Mesh(shoulderGeometry, armorMaterial);
            rightShoulder.position.set(0.35, 1.15, 0);
            group.add(rightShoulder);
            
            // Arms (with armor on upper arms)
            const upperArmGeometry = new THREE.BoxGeometry(0.22, 0.35, 0.22);
            const lowerArmGeometry = new THREE.BoxGeometry(0.18, 0.35, 0.18);
            
            // Left arm
            const leftUpperArm = new THREE.Mesh(upperArmGeometry, armorMaterial);
            leftUpperArm.position.set(-0.4, 1.0, 0);
            group.add(leftUpperArm);
            
            const leftLowerArm = new THREE.Mesh(lowerArmGeometry, skinMaterial);
            leftLowerArm.position.set(-0.4, 0.65, 0);
            group.add(leftLowerArm);
            
            // Right arm
            const rightUpperArm = new THREE.Mesh(upperArmGeometry, armorMaterial);
            rightUpperArm.position.set(0.4, 1.0, 0);
            group.add(rightUpperArm);
            
            const rightLowerArm = new THREE.Mesh(lowerArmGeometry, skinMaterial);
            rightLowerArm.position.set(0.4, 0.65, 0);
            group.add(rightLowerArm);
            
            // Legs (with armor on thighs)
            const thighGeometry = new THREE.BoxGeometry(0.27, 0.35, 0.27);
            const shinGeometry = new THREE.BoxGeometry(0.23, 0.35, 0.23);
            
            // Left leg
            const leftThigh = new THREE.Mesh(thighGeometry, armorMaterial);
            leftThigh.position.set(-0.18, 0.35, 0);
            group.add(leftThigh);
            
            const leftShin = new THREE.Mesh(shinGeometry, skinMaterial);
            leftShin.position.set(-0.18, 0.0, 0);
            group.add(leftShin);
            
            // Right leg
            const rightThigh = new THREE.Mesh(thighGeometry, armorMaterial);
            rightThigh.position.set(0.18, 0.35, 0);
            group.add(rightThigh);
            
            const rightShin = new THREE.Mesh(shinGeometry, skinMaterial);
            rightShin.position.set(0.18, 0.0, 0);
            group.add(rightShin);
            
            // Sword (human weapon)
            const swordBladeGeometry = new THREE.BoxGeometry(0.08, 0.7, 0.02);
            const swordBladeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xcccccc,
                metalness: 0.8,
                roughness: 0.2
            });
            const swordBlade = new THREE.Mesh(swordBladeGeometry, swordBladeMaterial);
            swordBlade.position.set(0.5, 0.9, 0);
            swordBlade.rotation.z = Math.PI / 8;
            group.add(swordBlade);
            
            const swordTipGeometry = new THREE.ConeGeometry(0.04, 0.1, 4);
            const swordTip = new THREE.Mesh(swordTipGeometry, swordBladeMaterial);
            swordTip.rotation.x = Math.PI / 2;
            swordTip.position.set(0.5, 1.25, 0);
            swordTip.rotation.z = Math.PI / 8;
            group.add(swordTip);
            
            const guardGeometry = new THREE.BoxGeometry(0.2, 0.05, 0.05);
            const guardMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x888888,
                metalness: 0.6,
                roughness: 0.4
            });
            const guard = new THREE.Mesh(guardGeometry, guardMaterial);
            guard.position.set(0.5, 0.65, 0);
            guard.rotation.z = Math.PI / 8;
            group.add(guard);
            
            const hiltGeometry = new THREE.BoxGeometry(0.06, 0.15, 0.06);
            const hiltMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x654321,
                roughness: 0.8
            });
            const hilt = new THREE.Mesh(hiltGeometry, hiltMaterial);
            hilt.position.set(0.5, 0.55, 0);
            hilt.rotation.z = Math.PI / 8;
            group.add(hilt);
            
            const pommelGeometry = new THREE.SphereGeometry(0.05, 8, 8);
            const pommelMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x666666,
                metalness: 0.7,
                roughness: 0.3
            });
            const pommel = new THREE.Mesh(pommelGeometry, pommelMaterial);
            pommel.position.set(0.5, 0.45, 0);
            pommel.rotation.z = Math.PI / 8;
            group.add(pommel);
        }
        
        return group;
    }
    
    get position() {
        return this.mesh.position;
    }
    
    set position(value) {
        this.mesh.position.copy(value);
        if (this.body) {
            this.body.position.set(value.x, value.y, value.z);
        }
    }
}
